import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '@symphenia/ui-components'
import { SITE_DOC_COUNTERSIGN_MAX, type SiteDocCountersignRequestView, type SiteDocProjectContact, type SiteDocSignerRole } from '@symphenia/shared-types'
import { fehlertext } from '@/lib/fehlertext'
import { fmtDate } from '@/lib/format-date'
import { cn } from '@/lib/utils'
import { createCountersignRequest } from '../api/site-docs.api'

const feld = 'h-10 w-full rounded-md border border-input bg-background px-3 text-sm'
const ROLLEN: SiteDocSignerRole[] = ['SUPERVISOR', 'CUSTOMER', 'OTHER']
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export interface GegenzeichnungDokument {
  id: string
  number: string | null
  documentDate: string
  title: string
}

/**
 * AP-48 6.3 (RN-6) — **„Zur Gegenzeichnung senden"**: bis zu 31 freigegebene Dokumente an einen Empfänger —
 * aus den Kontakten des Projekts oder frei (Name, Funktion, E-Mail, Rolle; Vorgabe Bauaufsicht). Der Versand
 * läuft über den Mailservice des Mandanten; die Bauaufsicht zeichnet im eigenen Browser.
 */
export function GegenzeichnungSendenDialog({
  dokumente,
  kontakte,
  onClose,
  onGesendet,
}: {
  dokumente: GegenzeichnungDokument[]
  kontakte: SiteDocProjectContact[]
  onClose: () => void
  onGesendet: (anfrage: SiteDocCountersignRequestView) => void
}) {
  const { t } = useTranslation()
  const [kontakt, setKontakt] = useState<number | null>(null)
  const [name, setName] = useState('')
  const [funktion, setFunktion] = useState('')
  const [email, setEmail] = useState('')
  const [rolle, setRolle] = useState<SiteDocSignerRole>('SUPERVISOR')
  const [fehler, setFehler] = useState<string | null>(null)
  const [arbeitet, setArbeitet] = useState(false)
  const zuViele = dokumente.length > SITE_DOC_COUNTERSIGN_MAX
  const emailGut = EMAIL.test(email.trim())
  const bereit = dokumente.length > 0 && !zuViele && emailGut && !arbeitet

  const waehle = (i: number) => {
    const k = kontakte[i]
    setKontakt(i)
    setName(k.name)
    setFunktion(k.function ?? '')
    setEmail(k.email ?? '')
  }

  async function senden() {
    setArbeitet(true)
    setFehler(null)
    try {
      const k = kontakt !== null ? kontakte[kontakt] : null
      const anfrage = await createCountersignRequest({
        documentIds: dokumente.map((d) => d.id),
        recipient: { email: email.trim(), name: name.trim() || null, function: funktion.trim() || null, contactId: k?.id && k.email?.trim() === email.trim() ? k.id : null, role: rolle },
      })
      onGesendet(anfrage)
    } catch (e) {
      setFehler(fehlertext(e, t))
      setArbeitet(false)
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={t('siteDocs.countersign.sendTitle')}
      size="md"
      footer={
        <div className="flex w-full justify-end gap-2">
          <button type="button" onClick={onClose} className="h-10 rounded-md border px-4 text-sm">
            {t('common.cancel')}
          </button>
          <button type="button" disabled={!bereit} onClick={() => void senden()} className="h-10 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground disabled:opacity-50">
            {t('siteDocs.countersign.send')}
          </button>
        </div>
      }
    >
      <div className="space-y-4 text-sm">
        {fehler && <p className="rounded-md bg-destructive/10 px-3 py-2 text-destructive">{fehler}</p>}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('siteDocs.countersign.documents', { count: dokumente.length })}</p>
          <ul className="mt-1 max-h-32 space-y-0.5 overflow-y-auto">
            {dokumente.map((d) => (
              <li key={d.id}>
                <span className="tabular-nums">{d.number ?? '—'}</span> · {fmtDate(d.documentDate)} · {d.title}
              </li>
            ))}
          </ul>
          {zuViele && <p className="mt-1 text-destructive">{t('siteDocs.countersign.tooMany', { max: SITE_DOC_COUNTERSIGN_MAX })}</p>}
        </div>
        {kontakte.length > 0 && (
          <div className="space-y-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('siteDocs.countersign.contacts')}</p>
            <ul className="max-h-40 space-y-1 overflow-y-auto">
              {kontakte.map((k, i) => (
                <li key={`${k.id ?? k.name}-${i}`}>
                  <button type="button" onClick={() => waehle(i)} className={cn('w-full rounded-md border px-3 py-1.5 text-left', kontakt === i && 'border-primary bg-primary/10')}>
                    <span className="font-medium">{k.name}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{[k.function, k.email].filter(Boolean).join(' · ')}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="flex flex-wrap gap-2" role="group" aria-label={t('siteDocs.countersign.role')}>
          {ROLLEN.map((r) => (
            <button key={r} type="button" aria-pressed={rolle === r} onClick={() => setRolle(r)} className={cn('h-9 rounded-md border px-3', rolle === r && 'border-primary bg-primary/10')}>
              {t(`siteDocs.roles.${r}`)}
            </button>
          ))}
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <input className={feld} aria-label={t('siteDocs.release.name')} placeholder={t('siteDocs.release.name')} value={name} onChange={(e) => setName(e.target.value)} />
          <input className={feld} aria-label={t('siteDocs.release.function')} placeholder={t('siteDocs.release.function')} value={funktion} onChange={(e) => setFunktion(e.target.value)} />
          <input className={cn(feld, 'sm:col-span-2')} type="email" aria-label={t('siteDocs.countersign.email')} placeholder={t('siteDocs.countersign.email')} value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        {email.trim() && !emailGut && <p className="text-destructive">{t('siteDocs.release.emailInvalid')}</p>}
        <p className="text-xs text-muted-foreground">{t('siteDocs.countersign.sendHint')}</p>
      </div>
    </Modal>
  )
}
